/**
 * Easy Protein® — Custom Cart Drawer
 * Cart API integration, rendering, subscription toggling, free box sync, progress bar.
 * Wrapped in an IIFE to avoid global scope pollution.
 */
(function () {
  'use strict';

  // ═══════════════════════════════════════════
  // Constants — Fill in after running /api/shopify-products
  // ═══════════════════════════════════════════

  // Change this assignment to VARIETY_BOX_BONUS_VARIANT_ID to switch the reward back.
  var BERRY_BURST_BONUS_VARIANT_ID = 49459675693208;
  var VARIETY_BOX_BONUS_VARIANT_ID = 47493326766232;
  var BONUS_GIFT_VARIANT_ID = BERRY_BURST_BONUS_VARIANT_ID;
  var BOTTLE_VARIETY_VARIANT_ID = 49459675627672;
  var BOTTLE_PRODUCT_IDS = [9264898343064, 9102151647384];
  var BULK_DISCOUNT_PRODUCT_IDS = [8750614642840, 8946615845016, 9102151647384, 9264898343064];
  var SHOTS_PRODUCT_ID = 8750614642840;
  var TBSPN_VARIANT_ID = 49502272913560;

  // Selling plan IDs (for subscription toggling)
  var SELLING_PLAN_30_DAY = 3691937944;
  var SELLING_PLAN_90_DAY = 3716219032;

  // ═══════════════════════════════════════════
  // DOM References
  // ═══════════════════════════════════════════

  var overlay = document.getElementById('ep-cart-overlay');
  var drawer = document.getElementById('ep-cart-drawer');
  var closeBtn = document.getElementById('ep-cart-close');
  var itemsContainer = document.getElementById('ep-cart-items');
  var footerContainer = document.getElementById('ep-cart-footer');
  var rewardsMsg = document.getElementById('ep-cart-rewards-msg');
  var progressFill = document.getElementById('ep-cart-progress-fill');

  var nodes = [
    document.getElementById('ep-cart-node-1'),
    document.getElementById('ep-cart-node-2'),
    document.getElementById('ep-cart-node-3')
  ];
  var labels = [
    document.getElementById('ep-cart-label-1'),
    document.getElementById('ep-cart-label-2'),
    document.getElementById('ep-cart-label-3')
  ];

  // State
  var busy = false; // prevents concurrent cart mutations

  // ═══════════════════════════════════════════
  // Cart API Helpers
  // ═══════════════════════════════════════════

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function changeQuantity(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { return r.json(); });
  }

  function addItem(variantId, qty, properties, sellingPlan) {
    var payload = { id: variantId, quantity: qty };
    if (properties) payload.properties = properties;
    if (sellingPlan) payload.selling_plan = sellingPlan;
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }

  function removeItem(key) {
    return changeQuantity(key, 0);
  }

  // ═══════════════════════════════════════════
  // Drawer Open / Close
  // ═══════════════════════════════════════════

  function openDrawer() {
    fetchCart().then(function (cart) {
      return syncFreeBox(cart);
    }).then(function (cart) {
      renderCart(cart);
      drawer.classList.add('ep-cart-open');
      overlay.classList.add('ep-cart-open');
      document.body.style.overflow = 'hidden';
    });
  }

  function closeDrawer() {
    drawer.classList.remove('ep-cart-open');
    overlay.classList.remove('ep-cart-open');
    document.body.style.overflow = '';
  }

  // Public API via custom events
  window.EpCart = { open: openDrawer, close: closeDrawer };

  // ═══════════════════════════════════════════
  // Progress Bar
  // ═══════════════════════════════════════════

  function updateProgress(cartState) {
    var totalUnits = Number((cartState && cartState.totalUnits) || cartState || 0);
    var hasSubscription = Boolean(cartState && cartState.hasSubscription);
    var qualifiesFreeShipping = Boolean(cartState && cartState.qualifiesFreeShipping);
    var freeShippingRemaining = Number((cartState && cartState.freeShippingRemaining) || 0);
    var hasVarietyPack = Boolean(cartState && cartState.hasVarietyPack);
    var bulkSubscriptionUnits = Number((cartState && cartState.bulkSubscriptionUnits) || 0);
    var isBulkSubscription = hasSubscription && bulkSubscriptionUnits > 0;
    var discountUnits = isBulkSubscription ? bulkSubscriptionUnits : totalUnits;

    // Reset all nodes/labels
    nodes.forEach(function (n) { if (n) n.classList.remove('ep-cart-reached'); });
    labels.forEach(function (l) { if (l) l.classList.remove('ep-cart-reached'); });

    if (isBulkSubscription) {
      if (labels[0]) labels[0].innerHTML = 'Extra 5%<br>2+ sub items';
      if (labels[1]) labels[1].innerHTML = 'Extra 10%<br>3+ sub items';
      if (labels[2]) labels[2].innerHTML = 'Bonus Gift<br>6+ Items';
    } else if (hasSubscription) {
      if (labels[0]) labels[0].innerHTML = 'Subscribed<br>Ships Free';
      if (labels[1]) labels[1].innerHTML = '15% Off<br>3+ Items';
      if (labels[2]) labels[2].innerHTML = 'Bonus Gift<br>6+ Items';
    } else {
      if (labels[0]) labels[0].innerHTML = '10% Off<br>2+ Items';
      if (labels[1]) labels[1].innerHTML = '15% Off<br>3+ Items';
      if (labels[2]) labels[2].innerHTML = 'Bonus Gift<br>6+ Items';
    }

    var fillPct = 0;
    var msg = '';

    if (totalUnits >= 6) {
      fillPct = 100;
      nodes.forEach(function (n) { if (n) n.classList.add('ep-cart-reached'); });
      labels.forEach(function (l) { if (l) l.classList.add('ep-cart-reached'); });
      msg = 'All Easy Protein® rewards are unlocked.';
    } else if (discountUnits >= 3) {
      fillPct = Math.round(62 + ((Math.min(totalUnits, 6) - 3) * (38 / 3)));
      if (nodes[0]) nodes[0].classList.add('ep-cart-reached');
      if (nodes[1]) nodes[1].classList.add('ep-cart-reached');
      if (labels[0]) labels[0].classList.add('ep-cart-reached');
      if (labels[1]) labels[1].classList.add('ep-cart-reached');
      var remaining = 6 - totalUnits;
      msg = hasVarietyPack
        ? 'Variety includes 3 bottles and its price includes 15% savings. <span class="ep-cart-hl">The free spoon does not count.</span>'
        : isBulkSubscription
          ? '<span class="ep-cart-hl">Extra 10% unlocked</span>'
          : '15% savings activated. Add <span class="ep-cart-hl">' + remaining + ' more</span> qualifying item' + (remaining === 1 ? '' : 's') + ' to unlock the bonus reward.';
    } else if (discountUnits >= 2) {
      fillPct = 30;
      if (nodes[0]) nodes[0].classList.add('ep-cart-reached');
      if (labels[0]) labels[0].classList.add('ep-cart-reached');
      msg = isBulkSubscription
        ? '<span class="ep-cart-hl">Extra 5% unlocked</span>'
        : '10% savings activated. Add <span class="ep-cart-hl">1 more Easy Protein® item</span> to unlock 15% off.';
    } else if (totalUnits === 1) {
      fillPct = hasSubscription && !isBulkSubscription ? 30 : 14;
      if (hasSubscription) {
        if (nodes[0]) nodes[0].classList.add('ep-cart-reached');
        if (labels[0]) labels[0].classList.add('ep-cart-reached');
        msg = 'Subscription perks activated: <span class="ep-cart-hl">15% off every delivery + free shipping</span>.';
      } else if (qualifiesFreeShipping) {
        msg = 'Free shipping unlocked. Add <span class="ep-cart-hl">1 more Easy Protein® item</span> to unlock 10% off.';
      } else {
        msg = 'Add <span class="ep-cart-hl">1 more Easy Protein® item</span> to unlock 10% off. <span class="ep-cart-hl">' + formatMoney(freeShippingRemaining) + '</span> away from free shipping.';
      }
    }

    // A subscription alone does not unlock a two-item bundle bonus.
    var reached = [
      isBulkSubscription ? bulkSubscriptionUnits >= 2 : hasSubscription || totalUnits >= 2,
      isBulkSubscription ? bulkSubscriptionUnits >= 3 : totalUnits >= 3,
      totalUnits >= 6
    ];
    nodes.forEach(function (node, index) { if (node) { node.classList.remove('ep-cart-reached'); if (reached[index]) node.classList.add('ep-cart-reached'); } });
    labels.forEach(function (label, index) { if (label) { label.classList.remove('ep-cart-reached'); if (reached[index]) label.classList.add('ep-cart-reached'); } });
    if (isBulkSubscription) {
      if (totalUnits >= 6 && bulkSubscriptionUnits < 3) msg = 'Bonus gift unlocked.';
      if (totalUnits >= 6 && bulkSubscriptionUnits >= 3) msg = '<span class="ep-cart-hl">Extra 10% + bonus gift unlocked</span>';
      if (totalUnits === 1) msg = '<span class="ep-cart-hl">15% off + free shipping</span>';
    }
    var milestoneLabels = labels[0] && labels[0].parentElement;
    if (milestoneLabels && milestoneLabels.parentElement) {
      var bundleNote = milestoneLabels.parentElement.querySelector('.ep-cart-bundle-note');
      if (!bundleNote) {
        bundleNote = document.createElement('div');
        bundleNote.className = 'ep-cart-bundle-note';
        bundleNote.style.cssText = 'font-size:11px;line-height:1.4;font-weight:400;color:#64748b;text-align:center;margin:10px 0 0;';
        milestoneLabels.insertAdjacentElement('afterend', bundleNote);
      }
      bundleNote.textContent = 'Bundle savings apply to the first delivery only.';
      bundleNote.hidden = !isBulkSubscription;
    }
    if (progressFill) progressFill.style.width = fillPct + '%';
    if (rewardsMsg) rewardsMsg.innerHTML = msg;
  }

  // ═══════════════════════════════════════════
  // Automatic Gift Sync
  // ═══════════════════════════════════════════

  function syncTbspnGift(cart) {
    if (!TBSPN_VARIANT_ID || !BOTTLE_PRODUCT_IDS.length) return Promise.resolve(cart);

    var hasBottle = cart.items.some(function (item) {
      return BOTTLE_PRODUCT_IDS.indexOf(Number(item.product_id)) !== -1 && !isFreeGift(item);
    });
    var spoonItems = cart.items.filter(function (item) {
      return Number(item.variant_id) === TBSPN_VARIANT_ID;
    });
    var autoGift = spoonItems.filter(function (item) {
      return item.properties && item.properties._is_tbspn_gift === 'true';
    })[0];

    if (hasBottle && spoonItems.length === 0) {
      return addItem(TBSPN_VARIANT_ID, 1, { _is_tbspn_gift: 'true' })
        .then(function () { return fetchCart(); });
    }

    if (!hasBottle && autoGift) {
      return removeItem(autoGift.key)
        .then(function () { return fetchCart(); });
    }

    // The tbspn is a one-per-order gift. Correct any quantity increase and
    // remove duplicate lines that could be created by repeated cart requests.
    if (hasBottle && spoonItems.length > 0) {
      var keepItem = autoGift || spoonItems[0];
      var updates = [];
      if (keepItem.quantity !== 1) updates.push({ key: keepItem.key, quantity: 1 });
      spoonItems.forEach(function (item) {
        if (item.key !== keepItem.key) updates.push({ key: item.key, quantity: 0 });
      });
      if (updates.length > 0) {
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: updates.reduce(function (result, update) {
              result[update.key] = update.quantity;
              return result;
            }, {})
          })
        }).then(function () { return fetchCart(); });
      }
    }

    return Promise.resolve(cart);
  }

  function syncFreeBoxReward(cart) {
    if (!BONUS_GIFT_VARIANT_ID) return Promise.resolve(cart);

    var totalUnits = 0;
    var freeItem = null;

    cart.items.forEach(function (item) {
      if (item.properties && item.properties._is_free_gift === 'true') {
        freeItem = item;
      } else {
        totalUnits += rewardUnitsForItem(item);
      }
    });

    function addBonusGift() {
      return addItem(BONUS_GIFT_VARIANT_ID, 1, { _is_free_gift: 'true' })
        .then(function () { return fetchCart(); });
    }

    if (totalUnits >= 6 && !freeItem) {
      return addBonusGift();
    }

    // Replace the previous Variety Box reward in carts that already unlocked it.
    if (totalUnits >= 6 && Number(freeItem.variant_id) !== BONUS_GIFT_VARIANT_ID) {
      return removeItem(freeItem.key).then(addBonusGift);
    }

    if (totalUnits < 6 && freeItem) {
      // Remove the bonus gift when the cart drops below the threshold.
      return removeItem(freeItem.key)
        .then(function () { return fetchCart(); });
    }

    return Promise.resolve(cart);
  }

  function syncFreeBox(cart) {
    return syncTbspnGift(cart).then(function (updatedCart) {
      return syncFreeBoxReward(updatedCart);
    });
  }

  // ═══════════════════════════════════════════
  // Subscription Toggling
  // ═══════════════════════════════════════════

  function convertToSubscription(lineKey, variantId, qty, sellingPlanId) {
    if (busy) return;
    busy = true;
    showLoading();
    // Remove current line, then re-add with selling plan
    removeItem(lineKey)
      .then(function () {
        return addItem(variantId, qty, null, sellingPlanId);
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) { return syncFreeBox(cart); })
      .then(function (cart) {
        renderCart(cart);
        busy = false;
      })
      .catch(function () { busy = false; refreshAndRender(); });
  }

  function changeFrequency(lineKey, variantId, qty, newSellingPlanId) {
    if (busy) return;
    busy = true;
    showLoading();
    removeItem(lineKey)
      .then(function () {
        return addItem(variantId, qty, null, newSellingPlanId);
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) { return syncFreeBox(cart); })
      .then(function (cart) {
        renderCart(cart);
        busy = false;
      })
      .catch(function () { busy = false; refreshAndRender(); });
  }

  function revertToOneTime(lineKey, variantId, qty) {
    if (busy) return;
    busy = true;
    showLoading();
    removeItem(lineKey)
      .then(function () {
        return addItem(variantId, qty);
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) { return syncFreeBox(cart); })
      .then(function (cart) {
        renderCart(cart);
        busy = false;
      })
      .catch(function () { busy = false; refreshAndRender(); });
  }

  // ═══════════════════════════════════════════
  // Quantity Change Handler
  // ═══════════════════════════════════════════

  function handleQuantityChange(key, newQty) {
    if (busy) return;
    busy = true;
    showLoading();
    changeQuantity(key, Math.max(1, newQty))
      .then(function () { return fetchCart(); })
      .then(function (cart) { return syncFreeBox(cart); })
      .then(function (cart) {
        renderCart(cart);
        busy = false;
      })
      .catch(function () { busy = false; refreshAndRender(); });
  }

  function handleRemove(key) {
    if (busy) return;
    busy = true;
    showLoading();
    removeItem(key)
      .then(function () { return fetchCart(); })
      .then(function (cart) { return syncFreeBox(cart); })
      .then(function (cart) {
        renderCart(cart);
        busy = false;
      })
      .catch(function () { busy = false; refreshAndRender(); });
  }

  // ═══════════════════════════════════════════
  // Rendering Helpers
  // ═══════════════════════════════════════════

  function showLoading() {
    if (itemsContainer) {
      itemsContainer.innerHTML = '<div class="ep-cart-loading"><div class="ep-cart-spinner"></div> Updating cart\u2026</div>';
    }
  }

  function refreshAndRender() {
    fetchCart()
      .then(function (cart) { return syncFreeBox(cart); })
      .then(function (cart) { renderCart(cart); });
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function isFreeGift(item) {
    return Boolean(item && item.properties && item.properties._is_free_gift === 'true');
  }

  function isBottleVarietyPack(item) {
    var variantId = Number(item && item.variant_id);
    var text = (((item && item.product_title) || '') + ' ' + ((item && item.variant_title) || '')).toLowerCase();
    return variantId === BOTTLE_VARIETY_VARIANT_ID ||
      (text.indexOf('variety') !== -1 && text.indexOf('bottle') !== -1 && (text.indexOf('30oz') !== -1 || text.indexOf('30 oz') !== -1));
  }

  function rewardUnitsForItem(item) {
    if (!item || isFreeGift(item) || Number(item.variant_id) === TBSPN_VARIANT_ID) return 0;
    return item.quantity * (isBottleVarietyPack(item) ? 3 : 1);
  }

  function cartSummary(cart) {
    var totalUnits = 0;
    var hasSubscription = false;
    var hasVarietyPack = false;
    var bulkSubscriptionUnits = 0;
    var freeShippingThreshold = 8000;

    (cart.items || []).forEach(function (item) {
      totalUnits += rewardUnitsForItem(item);
      if (isBottleVarietyPack(item)) hasVarietyPack = true;
      if (item.selling_plan_allocation) {
        hasSubscription = true;
        if (
          BULK_DISCOUNT_PRODUCT_IDS.indexOf(Number(item.product_id)) !== -1 &&
          !isBottleVarietyPack(item) &&
          Number(item.variant_id) !== TBSPN_VARIANT_ID &&
          !isFreeGift(item)
        ) {
          bulkSubscriptionUnits += Number(item.quantity || 0);
        }
      }
    });

    var subtotal = Number(cart.items_subtotal_price || cart.original_total_price || cart.total_price || 0);
    var total = Number(cart.total_price || 0);
    var qualifiesFreeShipping = hasSubscription || subtotal >= freeShippingThreshold || total >= freeShippingThreshold;

    return {
      totalUnits: totalUnits,
      hasSubscription: hasSubscription,
      hasVarietyPack: hasVarietyPack,
      bulkSubscriptionUnits: bulkSubscriptionUnits,
      qualifiesFreeShipping: qualifiesFreeShipping,
      freeShippingRemaining: Math.max(0, freeShippingThreshold - Math.max(subtotal, total))
    };
  }

  // ═══════════════════════════════════════════
  // Render: Single Item
  // ═══════════════════════════════════════════

  function renderItem(item) {
    var isGift = item.properties && item.properties._is_free_gift === 'true';
    var isTbspnGift = item.properties && item.properties._is_tbspn_gift === 'true';
    var isSub = !!item.selling_plan_allocation;
    var key = item.key;
    var variantId = item.variant_id;
    var qty = item.quantity;

    // Delete button
    var delBtn = '<button class="ep-cart-item-delete" data-action="remove" data-key="' + key + '" aria-label="Remove item">'
      + '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg></button>';

    // Image
    var imgHtml;
    if (isGift) {
      imgHtml = '<div class="ep-cart-item-img ep-cart-gift-img">\uD83C\uDF81</div>';
    } else if (item.image) {
      imgHtml = '<div class="ep-cart-item-img"><img src="' + item.image + '" alt="' + escapeHtml(item.product_title) + '" loading="lazy"></div>';
    } else {
      imgHtml = '<div class="ep-cart-item-img">\uD83D\uDCE6</div>';
    }

    // Title & variant
    var titleHtml = '<div class="ep-cart-item-title">' + escapeHtml(item.product_title) + '</div>';
    var variantHtml = item.variant_title
      ? '<div class="ep-cart-item-variant">Flavor: ' + escapeHtml(item.variant_title) + '</div>'
      : '';

    // Gift item — special rendering
    if (isGift) {
      var giftCode = Number(variantId) === BERRY_BURST_BONUS_VARIANT_ID
        ? 'ENJOYAFREEBOTTLE'
        : 'ENJOYAFREEBOX';
      return '<div class="ep-cart-item">'
        + imgHtml
        + '<div class="ep-cart-item-info">'
        + titleHtml + variantHtml
        + '<div class="ep-cart-gift-price">FREE</div>'
        + '<div class="ep-cart-badge"><span class="ep-cart-badge-dot"></span> ' + giftCode + '</div>'
        + '</div>'
        + delBtn
        + '</div>';
    }

    if (isTbspnGift) {
      var tbspnPrice = '<div class="ep-cart-gift-price">FREE</div>';
      return '<div class="ep-cart-item">'
        + imgHtml
        + '<div class="ep-cart-item-info">'
        + titleHtml + variantHtml
        + tbspnPrice
        + '<div class="ep-cart-badge"><span class="ep-cart-badge-dot"></span> FREE WITH BOTTLE ORDER</div>'
        + '</div>'
        + '</div>';
    }

    // Delivery frequency line (subscription items)
    var deliveryHtml = '';
    if (isSub && item.selling_plan_allocation) {
      var planName = item.selling_plan_allocation.selling_plan.name || '';
      if (SELLING_PLAN_30_DAY && item.selling_plan_allocation.selling_plan.id === SELLING_PLAN_30_DAY) {
        planName = 'Delivered Every 4 Weeks';
      }
      deliveryHtml = '<div class="ep-cart-item-delivery">' + escapeHtml(planName) + '</div>';
    }

    // Quantity stepper
    var qtyHtml = '<div class="ep-cart-qty">'
      + '<button data-action="qty-minus" data-key="' + key + '" data-qty="' + qty + '">\u2212</button>'
      + '<div class="ep-cart-qty-val">' + qty + '</div>'
      + '<button data-action="qty-plus" data-key="' + key + '" data-qty="' + qty + '">+</button>'
      + '</div>';

    // Subscribe button OR frequency dropdown
    var subControlHtml = '';
    if (isSub) {
      // Detect current frequency
      var currentPlan = item.selling_plan_allocation.selling_plan.id;
      var is30 = SELLING_PLAN_30_DAY && currentPlan === SELLING_PLAN_30_DAY;
      var is90 = SELLING_PLAN_90_DAY && currentPlan === SELLING_PLAN_90_DAY;

      subControlHtml = '<select class="ep-cart-freq-sel" data-action="freq-change" data-key="' + key + '" data-variant="' + variantId + '" data-qty="' + qty + '">'
        + '<option value="onetime">One-Time Purchase</option>'
        + '<option value="30"' + (is30 ? ' selected' : '') + '>Delivered Every 4 Weeks</option>'
        + '<option value="90"' + (is90 ? ' selected' : '') + '>Delivered Every 3 Months</option>'
        + '</select>';
    } else {
      var subSaveText = 'Subscribe &amp; Save 15%';
      subControlHtml = '<button class="ep-cart-sub-btn" data-action="subscribe" data-key="' + key + '" data-variant="' + variantId + '" data-qty="' + qty + '">'
        + subSaveText + '</button>';
    }

    return '<div class="ep-cart-item">'
      + imgHtml
      + '<div class="ep-cart-item-info">'
      + titleHtml + variantHtml + deliveryHtml
      + qtyHtml
      + subControlHtml
      + '</div>'
      + delBtn
      + '</div>';
  }

  // ═══════════════════════════════════════════
  // Render: Footer (discounts + totals)
  // ═══════════════════════════════════════════

  // Selling-plan savings are already included in Shopify's prices and are
  // excluded from cart.total_discount. Add them only to the savings display.
  function includedSubscriptionSavings(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      var allocation = item.selling_plan_allocation;
      if (!allocation || !Number.isFinite(Number(allocation.compare_at_price)) || !Number.isFinite(Number(allocation.price))) return sum;
      return sum + Math.max(0, Number(allocation.compare_at_price) - Number(allocation.price)) * Number(item.quantity || 0);
    }, 0);
  }

  function renderFooter(cart) {
    var discounts = cart.cart_level_discount_applications || [];
    var totalPrice = cart.total_price; // cents
    var subscriptionSavings = includedSubscriptionSavings(cart);
    var originalPrice = cart.original_total_price + subscriptionSavings; // Display reference only.
    var totalDiscount = cart.total_discount + subscriptionSavings; // Already included, not deducted again.

    function addDiscountRow(rows, title, amount) {
      if (!title || !amount) return;
      var existing = rows.filter(function (row) { return row.title === title; })[0];
      if (existing) {
        existing.amount += amount;
      } else {
        rows.push({ title: title, amount: amount });
      }
    }

    var summary = cartSummary(cart);
    var qualifiesFreeShipping = summary.qualifiesFreeShipping;

    // Build a deduped/aggregated discount list for the savings breakdown.
    var discountRows = [];
    addDiscountRow(discountRows, 'Subscription savings', subscriptionSavings);
    discounts.forEach(function (d) {
      addDiscountRow(discountRows, d.title, d.total_allocated_amount);
    });

    // Also include line-level discounts, aggregated by title so the same code
    // doesn't repeat across multiple cart lines.
    cart.items.forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (disc) {
        addDiscountRow(discountRows, disc.discount_application.title, disc.amount);
      });
    });

    var savingsPillHtml = totalDiscount > 0
      ? '<div class="ep-cart-savings-pill"><span><i></i> Discounts activated</span><strong>You saved ' + formatMoney(totalDiscount) + '</strong></div>'
      : '<div class="ep-cart-savings-pill ep-cart-savings-pill-empty"><span>No discounts yet</span></div>';

    // Price breakdown
    var priceHtml = '';
    var hasSavings = totalDiscount > 0;

    if (hasSavings) {
      priceHtml += '<div class="ep-cart-price-row ep-cart-orig">'
        + '<span>Original Price</span>'
        + '<span class="ep-cart-price-val">' + formatMoney(originalPrice) + '</span>'
        + '</div>';

      // Individual discount lines, deduped and summed by discount title
      discountRows.forEach(function (d) {
        priceHtml += '<div class="ep-cart-price-row ep-cart-sav">'
          + '<span>' + escapeHtml(d.title) + '</span>'
          + '<span class="ep-cart-price-val">-' + formatMoney(d.amount) + '</span>'
          + '</div>';
      });
    }

    // Free Shipping line (shown when order value or any subscription qualifies)
    if (qualifiesFreeShipping) {
      priceHtml += '<div class="ep-cart-price-row ep-cart-ship">'
        + '<span>Shipping</span>'
        + '<span class="ep-cart-price-val">Free</span>'
        + '</div>';
    }

    // Total
    priceHtml += '<div class="ep-cart-price-row ep-cart-total">'
      + '<span>New Total</span>'
      + '<span>' + formatMoney(totalPrice) + '</span>'
      + '</div>';

    // Checkout button
    var checkoutHtml = '<button class="ep-cart-checkout-btn" data-action="checkout">'
      + 'Checkout \u2022 ' + formatMoney(totalPrice) + '</button>';

    return savingsPillHtml
      + priceHtml
      + checkoutHtml;
  }

  // ═══════════════════════════════════════════
  // Render: Main
  // ═══════════════════════════════════════════

  function renderCart(cart) {
    if (!cart || !cart.items) return;

    // Empty cart
    if (cart.items.length === 0) {
      if (itemsContainer) {
        itemsContainer.innerHTML = '<div class="ep-cart-empty">'
          + '<svg viewBox="0 0 24 24" fill="#ccc"><path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42a.25.25 0 01-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1 1 0 0019.01 5H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>'
          + '<p>Your cart is empty</p></div>';
      }
      if (footerContainer) footerContainer.innerHTML = '';
      updateProgress(0);
      return;
    }

    updateProgress(cartSummary(cart));

    // Render items
    var html = '';
    cart.items.forEach(function (item) {
      html += renderItem(item);
    });
    if (itemsContainer) itemsContainer.innerHTML = html;

    // Render footer
    if (footerContainer) footerContainer.innerHTML = renderFooter(cart);
  }

  // ═══════════════════════════════════════════
  // Event Delegation (all clicks inside drawer)
  // ═══════════════════════════════════════════

  if (drawer) {
    drawer.addEventListener('click', function (e) {
      var target = e.target;
      // Walk up to find the data-action element
      while (target && target !== drawer && !target.getAttribute('data-action')) {
        target = target.parentElement;
      }
      if (!target || !target.getAttribute) return;

      var action = target.getAttribute('data-action');
      var key = target.getAttribute('data-key');
      var variantId = parseInt(target.getAttribute('data-variant'), 10);
      var qty = parseInt(target.getAttribute('data-qty'), 10);

      switch (action) {
        case 'remove':
          handleRemove(key);
          break;
        case 'qty-minus':
          if (qty > 1) handleQuantityChange(key, qty - 1);
          break;
        case 'qty-plus':
          handleQuantityChange(key, qty + 1);
          break;
        case 'subscribe':
          if (SELLING_PLAN_30_DAY) {
            convertToSubscription(key, variantId, qty, SELLING_PLAN_30_DAY);
          }
          break;
        case 'checkout':
          window.location.href = '/checkout';
          break;
      }
    });

    // Frequency dropdown change events
    drawer.addEventListener('change', function (e) {
      var target = e.target;
      if (!target.getAttribute || target.getAttribute('data-action') !== 'freq-change') return;

      var key = target.getAttribute('data-key');
      var variantId = parseInt(target.getAttribute('data-variant'), 10);
      var qty = parseInt(target.getAttribute('data-qty'), 10);
      var val = target.value;

      if (val === 'onetime') {
        revertToOneTime(key, variantId, qty);
      } else if (val === '30' && SELLING_PLAN_30_DAY) {
        changeFrequency(key, variantId, qty, SELLING_PLAN_30_DAY);
      } else if (val === '90' && SELLING_PLAN_90_DAY) {
        changeFrequency(key, variantId, qty, SELLING_PLAN_90_DAY);
      }
    });
  }

  // ═══════════════════════════════════════════
  // Close Drawer Events
  // ═══════════════════════════════════════════

  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (overlay) overlay.addEventListener('click', closeDrawer);

  // Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('ep-cart-open')) {
      closeDrawer();
    }
  });

  // ═══════════════════════════════════════════
  // Event Interception: Add-to-Cart Forms
  // ═══════════════════════════════════════════

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action');
    if (!action || action.indexOf('/cart/add') === -1) return;

    e.preventDefault();
    e.stopPropagation();

    var formData = new FormData(form);
    var payload = {};

    // Extract form fields
    var id = formData.get('id');
    var quantity = formData.get('quantity') || 1;
    var sellingPlan = formData.get('selling_plan');

    if (id) payload.id = parseInt(id, 10);
    payload.quantity = parseInt(quantity, 10) || 1;
    if (sellingPlan) payload.selling_plan = parseInt(sellingPlan, 10);

    // Extract properties
    var props = {};
    formData.forEach(function (val, key) {
      if (key.indexOf('properties[') === 0) {
        var propKey = key.replace('properties[', '').replace(']', '');
        props[propKey] = val;
      }
    });
    if (Object.keys(props).length > 0) payload.properties = props;

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        return fetchCart();
      })
      .then(function (cart) { return syncFreeBox(cart); })
      .then(function (cart) {
        renderCart(cart);
        openDrawerDirect();
      })
      .catch(function (err) {
        console.error('[EP Cart] Add to cart failed:', err);
      });
  }, true);

  // Open drawer without re-fetching (cart already in hand)
  function openDrawerDirect() {
    if (drawer) drawer.classList.add('ep-cart-open');
    if (overlay) overlay.classList.add('ep-cart-open');
    document.body.style.overflow = 'hidden';
  }

  // ═══════════════════════════════════════════
  // Event Interception: Cart Icon Clicks
  // ═══════════════════════════════════════════

  // 1. Direct bind to #CartButton (theme's cart icon button)
  var cartButton = document.getElementById('CartButton');
  if (cartButton) {
    cartButton.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      openDrawer();
    }, true);
  }

  // 2. Override Alpine.js $store.modals.open so 'cart' opens our drawer
  //    This catches any code path that tries to open the old cart modal
  function patchAlpineStore() {
    if (window.Alpine && window.Alpine.store) {
      var modalsStore = window.Alpine.store('modals');
      if (modalsStore && modalsStore.open) {
        var originalOpen = modalsStore.open.bind(modalsStore);
        modalsStore.open = function (name) {
          if (name === 'cart') {
            openDrawer();
            return;
          }
          return originalOpen(name);
        };
      }
    }
  }

  // Alpine may not be ready yet — try now and also on DOMContentLoaded / alpine:init
  patchAlpineStore();
  document.addEventListener('alpine:init', patchAlpineStore);
  document.addEventListener('alpine:initialized', patchAlpineStore);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(patchAlpineStore, 100);
    });
  } else {
    setTimeout(patchAlpineStore, 100);
  }

  // 3. Catch all /cart links and cart-related selectors
  document.addEventListener('click', function (e) {
    var target = e.target;
    var el = target;
    for (var i = 0; i < 6 && el; i++) {
      // <a href="/cart"> links
      if (el.tagName === 'A' && el.getAttribute('href') === '/cart') {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
        return;
      }
      // Theme cart button (backup if #CartButton direct bind missed)
      if (el.id === 'CartButton') {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
        return;
      }
      // Generic cart selectors
      if (el.classList && (
        el.classList.contains('cart-icon') ||
        el.classList.contains('cart-link') ||
        el.classList.contains('header__icon--cart') ||
        el.classList.contains('site-header__cart') ||
        el.getAttribute('data-cart-toggle') !== null
      )) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer();
        return;
      }
      el = el.parentElement;
    }
  }, true);

  // ═══════════════════════════════════════════
  // Custom Event Listener
  // ═══════════════════════════════════════════

  document.addEventListener('cart:open', function () {
    openDrawer();
  });

  // Also listen for Shopify's native cart events
  document.addEventListener('cart:refresh', function () {
    if (drawer && drawer.classList.contains('ep-cart-open')) {
      refreshAndRender();
    }
  });

})();
